import * as React from 'react';

import {TimelineEntryActionMenu} from 'shells/timeline/entry/toolbar/timeline-entry-action-menu';
import type {Reaction, Reactions} from 'shells/timeline/reaction-bar/types';

import type {EventFragmentFull} from 'nutshell-graphql-types';

import {ChatItems} from '../entry/chat-items';
import {useGetRelatedLeads} from '../graphql/hooks/use-get-related-leads';
import {
    getActorForChat,
    getEntryTypeFromPayload,
    getParticipantsForChat,
    getRelatedLeadsForChat,
} from '../helpers';
import {getCommentsWithFilteredReactions} from '../reactions/helpers';

import {TimelineEntryModal} from './timeline-entry-modal';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    currentUserId: string;
    event: EventFragmentFull;
    isLoading?: boolean;
    onToggleReaction: (
        changeLogId: string,
        reactionType: Reaction,
        filteredReactions: Reactions,
        parentChangeLogId?: string
    ) => any;
    filteredEntryReactions: Reactions;
    onAddComment: (value: string) => Promise<any>;
    onDeleteComment: (commentId: string) => Promise<any>;
    onDeleteEvent: (arg1: string) => Promise<any>;
};

export function TimelineChatModal(props: Props) {
    const {
        event,
        isOpen,
        onClose,
        isLoading,
        currentUserId,
        onToggleReaction,
        filteredEntryReactions,
        onAddComment,
        onDeleteComment,
        onDeleteEvent,
    } = props;
    const {payload} = event;

    const {relatedLeads} = useGetRelatedLeads({changeLogId: event.id});

    /**
     * We need to include this check, even though we are only passing an event
     * with a payload.__typename of 'Chat' because Apollo doesn't generate a specific
     * enough type when using a union type (like the TimelineEventPayload).
     * See this issue for more detail:
     * https://github.com/apollographql/apollo-tooling/issues/1223
     */
    if (payload.__typename === 'Chat') {
        const participants = getParticipantsForChat(payload.participants);
        const actor = getActorForChat(payload.participants, event.actor);

        const actionMenu = (
            <TimelineEntryActionMenu
                id={event.id}
                entryType={getEntryTypeFromPayload(payload)}
                handleDelete={(id) => onDeleteEvent(id).then(() => onClose())}
            />
        );

        return (
            <TimelineEntryModal
                isOpen={isOpen}
                onClose={onClose}
                isLoading={isLoading}
                iconVariant='chat'
                participantsForAvatarList={participants}
                timestamp={event.changeTime}
                actor={actor}
                participants={participants}
                entryType={getEntryTypeFromPayload(payload)}
                changeType={event.changeType}
                status={{
                    primary: {
                        level: 'info--grey',
                        text: 'Chat',
                    },
                }}
                relatedLeads={getRelatedLeadsForChat(relatedLeads || [])}
                content={<ChatItems chatItems={payload.chatItems} />}
                filteredEntryReactions={filteredEntryReactions}
                currentUserId={currentUserId}
                changeLogId={event.id}
                onToggleReaction={onToggleReaction}
                comments={getCommentsWithFilteredReactions(event.comments)}
                onAddComment={onAddComment}
                onDeleteComment={onDeleteComment}
                actionMenu={actionMenu}
            />
        );
    }

    return null;
}
